import { Download, Trash2, Eye, Link2, Loader2, Palette, ShoppingBag, ShoppingBasket, RotateCcw, GitBranch } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { CardTitle } from "./CardTitle";
import { TileImg } from "./TileImg";
import { DuckThumb } from "./DuckThumb";

export const statusLabel = (c) => {
  if (c.build_status === "ready") return "Print-ready";
  if (c.build_status === "queued" || c.build_status === "building") return "Sculpting 3D…";
  if (c.build_status === "waiting") return c.cap_blocked ? "Saved — waiting for a top-up" : c.ai_limit_blocked ? "Waiting for the AI service — resumes automatically" : c.gate_blocked ? "Queued — resumes automatically" : "Sculptor busy — auto-retrying";
  if (c.build_status === "views") return "Rendering side views";
  if (c.build_status === "views_review") return "Side views need your approval";
  if (c.build_status === "failed") return "3D build failed";
  if (c.build_status === "cancelled") return "3D build cancelled";
  return "Render preview";
};

export const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const Btn = ({ testId, onClick, icon: Icon, label, variant = "secondary", disabled, busy, title, className = "" }) => (
  <Button size="sm" variant={variant} className={`h-8 rounded-full gap-1 text-xs ${className}`} data-testid={testId} disabled={disabled} onClick={onClick} title={title}>
    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : Icon && <Icon className="h-3.5 w-3.5" />}
    {label}
  </Button>
);

// One duck in a list. `variant` "active" (My Ducks) or "archived" keeps the historical data-testid prefixes.
// `size` "lg" is the /my-ducks grid card: a 128x128 picture stacked above the details so a 4-column column never clips.
// Your Ducks adds `select` ({checked, onChange}: a corner checkbox), `note` (e.g. "Branch · v2 of …") and `extra` (one more action).
// `highlight` (the search text) marks it in the name and dims a family member shown only for context (c.match false).
// Active cards' pictures open the zoom viewer at the Front render.
export const DuckCard = ({ c, i, variant = "active", actions, size = "sm", select, note, extra = null, highlight }) => {
  const archived = variant === "archived";
  const big = size === "lg";
  const p = archived ? "archived" : "gallery";
  const building = ["queued", "building", "waiting"].includes(c.build_status);
  const { busy } = actions;
  const Pic = archived ? TileImg : DuckThumb;
  const context = highlight && c.match === false;
  return (
    <div
      data-testid={`${archived ? "archived-card" : "session-gallery-card"}-${i}`}
      className={`rounded-2xl border p-3 flex gap-3 min-w-0 transition-shadow duration-200 hover:shadow-md ${big ? "flex-col items-start" : "items-center"} ${archived ? "border-dashed border-border bg-muted/30" : "border-border bg-card"} ${select?.checked ? "ring-2 ring-accent" : highlight && !context ? "ring-2 ring-primary/70" : ""} ${context ? "opacity-60" : ""}`}
      data-match={highlight ? String(!context) : undefined}
    >
      <div className="relative shrink-0">
        <Pic
          c={c}
          tile={c.tile}
          buttonTestId={`${p}-thumb-btn-${i}`}
          size="256"
          px={big ? 128 : 64}
          alt="duck render"
          testId={`${p}-tile-${i}`}
          className={`${big ? "h-32 w-32" : "h-16 w-16"} rounded-xl object-cover border border-border bg-muted/40 ${archived ? "opacity-90" : ""}`}
        />
        {select && (
          <Checkbox
            checked={select.checked}
            onCheckedChange={select.onChange}
            aria-label={`Select ${c.name || "duck"}`}
            data-testid={`${p}-select-${i}`}
            className="absolute left-2 top-2 h-5 w-5 rounded-md bg-background/90 shadow"
          />
        )}
      </div>
      <div className="flex-1 min-w-0 w-full">
        <CardTitle c={c} i={i} prefix={p} actions={actions} canRename={!archived && !!actions.rename} highlight={highlight} fallback={`${c.style} ${c.figure === "full" ? "full duck" : "bust"}`} />
        {note && (
          <p className="text-[11px] text-accent font-mono2 flex items-center gap-1 truncate" data-testid={`${p}-note-${i}`}>
            <GitBranch className="h-3 w-3 shrink-0" /> {note}
          </p>
        )}
        {!archived && (
          <p className="text-xs text-muted-foreground font-mono2 flex items-center gap-1" data-testid={`gallery-status-${i}`}>
            {building && <Loader2 className="h-3 w-3 animate-spin" />}
            {statusLabel(c)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground/80 font-mono2 mt-0.5" data-testid={`${p}-date-${i}`}>
          {archived ? (c.deleted_at ? `Archived ${fmtDate(c.deleted_at)}` : "Archived") : c.created_at ? `Created ${fmtDate(c.created_at)}` : ""}
        </p>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          <Btn testId={`${p}-open-btn-${i}`} onClick={() => actions.open(c)} icon={Eye} label="Open" />
          {(c.has_model || archived) && (
            <Btn testId={`${p}-buy-btn-${i}`} variant="default" className="font-bold" busy={busy.buying === c.id} disabled={busy.buying === c.id} onClick={() => actions.buy(c)} icon={ShoppingBag} label={archived ? "Reorder" : "Buy"} title="Put this duck in your cart at 50 mm" />
          )}
          {c.has_model && !archived && (
            <Btn testId={`gallery-basket-btn-${i}`} disabled={actions.basketBusy} onClick={() => actions.basketAdd([c.id])} icon={ShoppingBasket} label="Basket" title="Save to your basket at 50 mm and check out later" />
          )}
          {c.has_model && <Btn testId={archived ? `archived-3mf-btn-${i}` : `download-3mf-gallery-btn-${i}`} onClick={() => actions.download3mf(c)} icon={Download} label="3MF" />}
          {c.has_model && !archived && <Btn testId={`download-stl-gallery-btn-${i}`} variant="outline" onClick={() => actions.downloadStl(c)} label="STL" />}
          {c.has_model && !archived && <Btn testId={`gallery-share-btn-${i}`} variant="ghost" className="w-8 p-0" onClick={() => actions.share(c)} icon={Link2} />}
          {c.has_model && !archived && (
            <Btn testId={`gallery-repaint-btn-${i}`} variant="ghost" busy={busy.repainting === c.id} disabled={busy.repainting === c.id} onClick={() => actions.repaint(c)} icon={Palette} label="Re-paint" title="Re-analyse colours & re-render the turntable (no re-sculpt)" />
          )}
          {!archived && extra}
          {archived ? (
            <Btn testId={`archived-restore-btn-${i}`} variant="outline" busy={busy.restoring === c.id} disabled={busy.restoring === c.id} onClick={() => actions.restore(c)} icon={RotateCcw} label="Restore" />
          ) : (
            <Btn testId={`gallery-delete-btn-${i}`} variant="ghost" className="w-8 p-0 text-destructive" onClick={() => actions.del(c.id)} icon={Trash2} />
          )}
        </div>
      </div>
    </div>
  );
};
